import { getPlpServiceabilityAPI, getPdpWidgetAPI } from "../../api/plpApi";
import { COUNTRIES } from "../../constants/common/common";
import { LAST_VISITED_PINCODE } from "../../constants/storage";
import CookieUtil from "../storage/cookieUtil";

/**
 * Resolves product video URL from widget API shapes (`videoUrl` or `video.url`).
 *
 * @param {Object} product
 * @returns {string}
 */
export const getProductVideoUrl = (product) =>
  product?.videoUrl ||
  product?.video?.url ||
  product?.videoLink ||
  product?.productVideoLink ||
  product?.productContent?.productVideoLink ||
  "";

/**
 * Normalizes widget product fields used by ProductCard.
 *
 * @param {Object} product
 * @returns {Object}
 */
export const normalizeWidgetProduct = (product) => {
  const videoUrl = getProductVideoUrl(product);

  return {
    ...product,
    videoUrl,
  };
};

/**
 * Normalizes an array of widget products.
 *
 * @param {Array<Object>} products
 * @returns {Array<Object>}
 */
export const normalizeWidgetProducts = (products) => {
  if (!Array.isArray(products)) {
    return [];
  }

  return products.map(normalizeWidgetProduct);
};

/**
 * Resolves pincode for PDP widget APIs based on domestic vs international catalog.
 *
 * @param {Object} params
 * @param {string} [params.catalogId]
 * @param {string} [params.pincodeFromState]
 * @param {Object} [params.selectedIntLocation]
 * @returns {string}
 */
export const getPdpWidgetPincode = ({ catalogId, pincodeFromState, selectedIntLocation }) => {
  const isInternational = catalogId && catalogId !== COUNTRIES.INDIA;

  if (isInternational) {
    return selectedIntLocation?.pincode || "";
  }

  return pincodeFromState || CookieUtil.getCookie(LAST_VISITED_PINCODE) || "";
};

/**
 * Collects unique product IDs from one or more product arrays.
 *
 * @param {...Array<Object>} productLists
 * @returns {string}
 */
export const getProductIdsFromLists = (...productLists) => {
  const ids = new Set();

  productLists.flat().forEach((product) => {
    if (product?.productId) {
      ids.add(product.productId);
    }
  });

  return [...ids].join(",");
};

/**
 * Merges earliest delivery dates into product objects.
 *
 * @param {Array<Object>} products
 * @param {Object} deliveryDates
 * @returns {Array<Object>}
 */
export const enrichProductsWithDeliveryDates = (products, deliveryDates = {}) => {
  if (!Array.isArray(products)) {
    return [];
  }

  return products.map((product) => {
    const deliveryEntry = deliveryDates[product?.productId];

    return {
      ...product,
      deliveryEntry: deliveryEntry || null,
    };
  });
};

/**
 * Fetches earliest delivery dates for multiple product lists in a single API call.
 *
 * @param {string} pincode
 * @param {...(Array<Object>|{ lat?: number|string, long?: number|string })} args - Product lists and optional lat/long options
 * @returns {Promise<Object>}
 */
export const fetchEarliestDeliveryDatesMap = async (pincode, ...args) => {
  if (!pincode) {
    return {};
  }

  const productLists = [];
  let latLong = {};

  args.forEach((arg) => {
    if (Array.isArray(arg)) {
      productLists.push(arg);
      return;
    }

    if (arg && typeof arg === "object") {
      latLong = arg;
    }
  });

  const productIds = getProductIdsFromLists(...productLists);

  if (!productIds) {
    return {};
  }

  const { lat, long } = latLong;

  try {
    const response = await getPlpServiceabilityAPI(productIds, pincode, { lat, long });
    return response?.deliveryDates || {};
  } catch (error) {
    console.error("Failed to fetch earliest delivery dates:", error);
    return {};
  }
};

/**
 * Returns true when widget API response has a valid products array.
 *
 * @param {Object} response
 * @returns {boolean}
 */
export const isValidPdpWidgetResponse = (response) => {
  return !response?.status && Array.isArray(response?.products) && response.products.length > 0;
};

/**
 * Fetches a PDP widget, optionally renders products before delivery enrichment, then fetches earliest delivery.
 *
 * @param {Object} params
 * @param {string} params.widgetType
 * @param {string} params.productId
 * @param {string} [params.pincode]
 * @param {string} [params.geo]
 * @param {number} [params.page]
 * @param {number} [params.size]
 * @param {number|string} [params.lat]
 * @param {number|string} [params.long]
 * @param {Function} [params.onProductsLoaded] - Called with products before delivery API (enables video playback).
 * @returns {Promise<{ widgetName: string, products: Array<Object> }>}
 */
export const fetchAndEnrichPdpWidget = async ({
  widgetType,
  productId,
  pincode,
  geo,
  page,
  size,
  lat,
  long,
  onProductsLoaded,
}) => {
  const response = await getPdpWidgetAPI({ widgetType, productId, pincode, geo, page, size });

  if (!isValidPdpWidgetResponse(response)) {
    return {
      widgetName: response?.widgetName || "",
      products: [],
      currentPage: response?.currentPage ?? page ?? 0,
      totalPages: response?.totalPages ?? 0,
    };
  }

  const products = normalizeWidgetProducts(response.products);
  const widgetName = response.widgetName || "";
  const partialResult = { widgetName, products };

  onProductsLoaded?.(partialResult);

  const deliveryDates = await fetchEarliestDeliveryDatesMap(pincode, { lat, long }, products);

  return {
    widgetName,
    products: enrichProductsWithDeliveryDates(products, deliveryDates),
    currentPage: response?.currentPage ?? page ?? 0,
    totalPages: response?.totalPages ?? 0,
  };
};

/**
 * Fetches recently viewed products and enriches them with earliest delivery sequentially.
 *
 * @param {string[]} productIds
 * @param {string} pincode
 * @param {Function} getProductsByIds - API fn returning product list response
 * @param {Function} parseProducts - Parses API response to product array
 * @param {Function} [onProductsLoaded]
 * @param {Object} [options]
 * @param {number|string} [options.lat]
 * @param {number|string} [options.long]
 * @returns {Promise<Array<Object>>}
 */
export const fetchAndEnrichRecentlyViewedProducts = async (
  productIds,
  pincode,
  getProductsByIds,
  parseProducts,
  onProductsLoaded,
  { lat, long } = {},
) => {
  if (!Array.isArray(productIds) || productIds.length === 0) {
    return [];
  }

  const response = await getProductsByIds(productIds);
  const products = normalizeWidgetProducts(parseProducts(response));

  onProductsLoaded?.(products);

  const deliveryDates = await fetchEarliestDeliveryDatesMap(pincode, { lat, long }, products);

  return enrichProductsWithDeliveryDates(products, deliveryDates);
};
